import { AsyncPipe, isPlatformBrowser } from '@angular/common'
import type { ElementRef, OnInit, OutputEmitterRef } from '@angular/core'
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  HostListener,
  Input,
  PLATFORM_ID,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { BehaviorSubject, map } from 'rxjs'
import {
  debounceTime,
  distinctUntilChanged,
  skip,
  switchMap,
  take,
} from 'rxjs/operators'

import { AuthorsService } from '../../api/authors-service/authors.service'
import { MagazineService } from '../../api/magazine-service/magazine.service'
import type { MagazineDto } from '../../api/responses'
import { AuthorSearchService } from '../../services/author-search.service'
import { FilterDialogService } from '../../services/filter-dialog.service'
import { FilterSelectionService } from '../../services/filter-selection.service'
import { HomeStateService } from '../../services/home-services/home-state.service'
import { FormatAuthorNamePipe } from '../../shared/pipes/format-author-name.pipe'
import type {
  AuthorResponse,
  EmitEvents,
  EmitterName,
  FilterAuthor,
  FilterCategory,
  FilterDialogConfig,
  FilterEmitEvents,
  FilterItemType,
  FilterMagazine,
  FilterSubscription,
  FilterType,
} from '../../types/types'
import { ArticleType } from '../../types/types'
import { GenericFilterComponent } from './components/generic-filter/generic-filter.component'
import { MobileFilterComponent } from './components/mobile-filter/mobile-filter.component'

const AUTHOR_PAGE_SIZE = 150

@Component({
  selector: 'dhp-filter',
  templateUrl: './filter.component.html',
  styleUrls: ['./filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [FormatAuthorNamePipe],
  imports: [
    GenericFilterComponent,
    MobileFilterComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class FilterComponent implements OnInit, EmitEvents {
  readonly showOnlyMagazines = input(false)
  readonly modalStateChange = output<FilterEmitEvents['modalStateChange']>()
  readonly magazineSelected = output<FilterEmitEvents['magazineSelected']>()
  readonly authorSelected = output<FilterEmitEvents['authorSelected']>()
  readonly typeSelected = output<FilterEmitEvents['typeSelected']>()
  readonly filterDialog =
    viewChild.required<ElementRef<HTMLDialogElement>>('filterDialog')
  isLoadingMagazines = false
  isLoadingTypes = false
  isLoadingAuthors = false
  showMobileFilterDialog = false
  mobileExpandedSections = {
    magazines: true,
    types: false,
    authors: false,
  }

  magazines: FilterMagazine[] = []
  authors: FilterAuthor[] = []
  types: FilterType[] = []
  private readonly destroyRef = inject(DestroyRef)
  private readonly platformId = inject(PLATFORM_ID)
  private readonly magazineService = inject(MagazineService)
  private readonly dialogService = inject(FilterDialogService)
  readonly dialogConfig$ = this.dialogService.dialogConfig$
  private readonly authorSearch = inject(AuthorSearchService)
  readonly searchConfig$ = this.authorSearch.searchConfig$
  private readonly filterSelection = inject(FilterSelectionService)
  readonly magazines$ = this.filterSelection.magazines$
  readonly authors$ = this.filterSelection.authors$
  readonly types$ = this.filterSelection.types$
  readonly selectedMagazinesCount$ =
    this.filterSelection.getSelectedCount('magazines')
  readonly selectedAuthorsCount$ =
    this.filterSelection.getSelectedCount('authors')
  readonly selectedTypesCount$ = this.filterSelection.getSelectedCount('types')
  private readonly cdr = inject(ChangeDetectorRef)
  private readonly homeState = inject(HomeStateService)
  readonly totalArticles$ = this.homeState.totalDocs$

  private readonly authorsService = inject(AuthorsService)
  private readonly formatAuthorNamePipe = inject(FormatAuthorNamePipe)
  private readonly translate = inject(TranslateService)
  private readonly searchTerms$ = new BehaviorSubject<string>('')
  private searchSubscription: FilterSubscription | null = null
  private magazinesLoaded = false
  private magazinePage = 1
  private totalMagazines: number | null = null

  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input() set selectedMagazineIds(ids: number[] | null) {
    if (ids) {
      this.updateSelectedMagazines(ids)
    }
  }

  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input() set selectedAuthorIds(ids: number[] | null) {
    if (ids) {
      this.updateSelectedAuthors(ids)
    }
  }

  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input() set selectedArticleTypes(types: ArticleType[] | null) {
    if (types) {
      this.updateSelectedTypes(types)
    }
  }

  ngOnInit(): void {
    this.initializeSubscriptions()
    this.loadInitialData()
  }

  onAuthorSearch(term: string): void {
    this.searchTerms$.next(term)
  }

  toggleDialog(type: keyof FilterDialogConfig): void {
    const wasOpen = this.dialogService.getDialogState(type)

    this.dialogService.toggleDialog(type)

    if (!wasOpen && this.dialogService.getDialogState(type)) {
      if (type === 'showTypeDialog') {
        this.ensureTypesPopulated()
      }

      if (type === 'showAuthorDialog') {
        this.setupAuthorDialog()
      }

      const category = this.getCategoryFromDialogType(type)
      if (category) {
        this.emitSelectedItems(category)
      }
    } else if (wasOpen && type === 'showAuthorDialog') {
      this.cleanupAuthorSearch()
    }
  }

  toggleSelection(
    category: FilterCategory,
    item: FilterMagazine | FilterAuthor | FilterType,
    event?: Event
  ): void {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    this.updateItemSelection(category, item)
    this.keepDialogOpen(category)
    this.cdr.detectChanges()
  }

  clearSelection(category: FilterCategory): void {
    this.filterSelection.clearSelection(category)
    this.emitSelectedItems(category)
    this.cdr.detectChanges()
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!isPlatformBrowser(this.platformId) || !this.isAnyDialogOpen()) {
      return
    }

    if (this.isClickInsideDialog(event)) {
      event.preventDefault()
      event.stopPropagation()
      return
    }

    this.dialogService.closeAllDialogs()
  }

  resetAllFilters(): void {
    ;['magazines', 'authors', 'types'].forEach(category => {
      this.filterSelection.clearSelection(category as FilterCategory)
      this.emitSelectedItems(category as FilterCategory)
    })
    this.cdr.detectChanges()
  }

  toggleMobileSection(section: keyof typeof this.mobileExpandedSections): void {
    this.mobileExpandedSections[section] = !this.mobileExpandedSections[section]

    if (section === 'authors' && this.mobileExpandedSections[section]) {
      this.setupAuthorDialog()
    } else if (section === 'types' && this.mobileExpandedSections[section]) {
      this.loadTypes()
    }

    this.cdr.detectChanges()
  }

  onMobileScroll(event: Event, category: FilterCategory): void {
    const element = event.target as HTMLElement
    const reachedBottom =
      element.scrollHeight - element.scrollTop <= element.clientHeight + 100

    if (reachedBottom) {
      this.loadMoreIfNeeded(category)
    }
  }

  loadMoreIfNeeded(category: FilterCategory): void {
    if (this.isLoading(category)) return
    this.loadMoreItems(category)
  }

  openMobileFilterDialog(): void {
    this.showMobileFilterDialog = true
    this.mobileExpandedSections = {
      magazines: true,
      types: false,
      authors: false,
    }
    Promise.resolve().then(() => {
      this.cdr.markForCheck()
      this.cdr.detectChanges()
    })
  }

  closeMobileFilterDialog(): void {
    this.showMobileFilterDialog = false
    Promise.resolve().then(() => {
      this.cdr.markForCheck()
      this.cdr.detectChanges()
    })
  }

  applyMobileFilters(): void {
    this.closeMobileFilterDialog()
  }

  private initializeSubscriptions(): void {
    // State changes subscription
    this.filterSelection
      .getState()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.cdr.detectChanges()
      })

    // Author search config subscription
    this.authorSearch.searchConfig$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map(config => config.isLoadingAuthors)
      )
      .subscribe(isLoading => {
        this.isLoadingAuthors = isLoading
        this.cdr.detectChanges()
      })

    // Dialog config subscription
    this.dialogConfig$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(config => {
        if (config?.showAuthorDialog) {
          this.setupAuthorDialog()
        }
      })

    // Magazines subscription
    this.magazines$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(magazines => {
        this.handleMagazinesUpdate(magazines)
      })

    // Authors subscription
    this.authors$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(authors => {
        this.handleAuthorsUpdate(authors)
      })

    // Types subscription
    this.types$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(types => {
      this.handleTypesUpdate(types)
    })

    // Search terms subscription
    this.setupAuthorSearchSubscription()
  }

  private loadInitialData(): void {
    this.loadMagazines()
    this.loadTypes()
    this.loadAuthors()
  }

  private handleMagazinesUpdate(magazines: FilterMagazine[] | null): void {
    if ((magazines?.length ?? 0) > 0 || !this.magazinesLoaded) {
      this.magazines = magazines ?? []
      this.magazinesLoaded = (magazines?.length ?? 0) > 0
    }
    this.cdr.detectChanges()
  }

  private handleAuthorsUpdate(authors: FilterAuthor[] | null): void {
    this.authors = authors ?? []
    this.cdr.detectChanges()
  }

  private handleTypesUpdate(types: FilterType[] | null): void {
    this.types = types ?? []
    this.cdr.detectChanges()
  }

  private updateSelectedMagazines(ids: number[]): void {
    const magazines =
      (this.filterSelection.magazines$ as BehaviorSubject<FilterMagazine[]>)
        .value ?? []
    magazines.forEach(magazine => {
      magazine.selected = ids.includes(magazine.id)
    })
    this.filterSelection.updateMagazines(magazines)
  }

  private updateSelectedAuthors(ids: number[]): void {
    const authors =
      (this.filterSelection.authors$ as BehaviorSubject<FilterAuthor[]>)
        .value ?? []
    authors.forEach(author => {
      author.selected = ids.includes(author.id)
    })
    this.filterSelection.updateAuthors(authors)
  }

  private updateSelectedTypes(types: ArticleType[]): void {
    const currentTypes =
      (this.filterSelection.types$ as BehaviorSubject<FilterType[]>).value ?? []
    currentTypes.forEach(type => {
      type.selected = types.includes(type.id as ArticleType)
    })
    this.filterSelection.updateTypes(currentTypes)
  }

  private setupAuthorSearchSubscription(): void {
    this.searchTerms$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        // searchTerms$ is a BehaviorSubject seeded with '', which would
        // otherwise trigger a redundant getAuthors call identical to the
        // initial loadAuthors() fetch as soon as this subscribes.
        skip(1),
        debounceTime(300),
        map(term => term.trim()),
        distinctUntilChanged(),
        switchMap(term => {
          this.isLoadingAuthors = term !== ''
          this.cdr.detectChanges()
          return this.authorSearch.search(term)
        })
      )
      .subscribe({
        next: authors => {
          this.handleAuthorSearchResults(authors)
        },
        error: () => {
          this.filterSelection.updateAuthors([])
          this.isLoadingAuthors = false
          this.cdr.detectChanges()
        },
      })
  }

  private handleAuthorSearchResults(authors: FilterAuthor[] | null): void {
    if (!authors) {
      this.filterSelection.updateAuthors([])
      return
    }

    const currentAuthors = this.filterSelection.getCurrentAuthors()
    const selectedIds = currentAuthors.filter(a => a.selected).map(a => a.id)

    const newAuthors = this.mapAuthorsToFilterAuthors(authors, selectedIds)
    const selectedAuthorsNotInSearch = this.getSelectedAuthorsNotInSearch(
      currentAuthors,
      newAuthors
    )

    this.filterSelection.updateAuthors([
      ...selectedAuthorsNotInSearch,
      ...newAuthors,
    ])
    this.isLoadingAuthors = false
    this.cdr.detectChanges()
  }

  private mapAuthorsToFilterAuthors(
    authors: FilterAuthor[],
    selectedIds: number[]
  ): FilterAuthor[] {
    return authors.map(author => ({
      id: author.id,
      name: this.formatAuthorNamePipe.transform(author),
      selected: selectedIds.includes(author.id),
      additionalProps: {
        first_name: author.additionalProps?.first_name,
        last_name: author.additionalProps?.last_name,
        pre_title: author.additionalProps?.pre_title,
        middle_name: author.additionalProps?.middle_name,
        initials: author.additionalProps?.initials,
        post_title: author.additionalProps?.post_title,
      },
    }))
  }

  private getSelectedAuthorsNotInSearch(
    currentAuthors: FilterAuthor[],
    newAuthors: FilterAuthor[]
  ): FilterAuthor[] {
    return currentAuthors.filter(
      author => author.selected && !newAuthors.some(m => m.id === author.id)
    )
  }

  private loadMagazines(): void {
    if (this.magazines.length > 0) return

    this.isLoadingMagazines = true
    this.cdr.detectChanges()

    this.magazineService
      .getMagazines(true, true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.handleMagazinesResponse(response)
        },
        error: () => {
          this.isLoadingMagazines = false
          this.cdr.detectChanges()
        },
      })
  }

  private handleMagazinesResponse(response: {
    items: MagazineDto[]
    total?: number
  }): void {
    this.totalMagazines = response.total ?? null

    if (response?.items?.length > 0) {
      const magazines = this.mapMagazinesToFilterMagazines(response.items)
      if (magazines.length > 0 && this.magazines.length === 0) {
        this.filterSelection.updateMagazines(magazines)
        this.magazines = magazines
        this.magazinesLoaded = true
      }
    } else if (this.magazines.length === 0) {
      this.filterSelection.updateMagazines([])
    }

    this.isLoadingMagazines = false
    this.cdr.detectChanges()
  }

  private mapMagazinesToFilterMagazines(
    items: MagazineDto[]
  ): FilterMagazine[] {
    return items
      .map(magazine => {
        if (magazine?.id) {
          return {
            name: magazine.title || `Magazine ${magazine.id}`,
            selected: false,
            id: magazine.id,
          }
        }
        return null
      })
      .filter((m): m is FilterMagazine => m !== null)
  }

  private loadTypes(): void {
    this.isLoadingTypes = true
    this.cdr.detectChanges()

    const types = this.createFilterTypesFromEnum()
    const currentTypes = this.filterSelection.getCurrentTypes()

    if (currentTypes?.length > 0) {
      this.updateExistingTypes(currentTypes, types)
    } else {
      this.filterSelection.updateTypes(types)
    }

    this.isLoadingTypes = false
    this.cdr.detectChanges()
  }

  private createFilterTypesFromEnum(): FilterType[] {
    return Object.values(ArticleType).map(type => ({
      id: type,
      name: this.formatArticleTypeName(type),
      selected: false,
    }))
  }

  private updateExistingTypes(
    currentTypes: FilterType[],
    newTypes: FilterType[]
  ): void {
    const existingTypeIds = new Set(currentTypes.map(t => t.id))
    const missingTypes = newTypes.filter(t => !existingTypeIds.has(t.id))

    if (missingTypes.length > 0) {
      this.filterSelection.updateTypes([...currentTypes, ...missingTypes])
    }
  }

  private formatArticleTypeName(type: string): string {
    return String(this.translate.instant('articleTypes.' + type))
  }

  private loadAuthors(): void {
    this.isLoadingAuthors = true
    this.cdr.detectChanges()

    this.authorsService
      .getAuthors(1, AUTHOR_PAGE_SIZE)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.handleAuthorsResponse(response)
        },
        error: () => {
          this.filterSelection.updateAuthors([])
          this.isLoadingAuthors = false
          this.cdr.detectChanges()
        },
      })
  }

  private handleAuthorsResponse(response: AuthorResponse): void {
    if (response?.Authors?.docs) {
      const authors = this.mapAuthorsResponseToFilterAuthors(
        response.Authors.docs
      )
      this.filterSelection.updateAuthors(authors)
    } else {
      this.filterSelection.updateAuthors([])
    }

    this.isLoadingAuthors = false
    this.cdr.detectChanges()
  }

  private mapAuthorsResponseToFilterAuthors(
    docs: {
      id: number
      first_name?: string
      last_name?: string
      pre_title?: string
      middle_name?: string
      initials?: string
      post_title?: string
    }[]
  ): FilterAuthor[] {
    return docs.map(author => ({
      id: author.id,
      name: this.formatAuthorNamePipe.transform(author),
      selected: false,
      additionalProps: {
        first_name: author.first_name,
        last_name: author.last_name,
        pre_title: author.pre_title,
        middle_name: author.middle_name,
        initials: author.initials,
        post_title: author.post_title,
      },
    }))
  }

  private ensureTypesPopulated(): void {
    const currentTypes = this.filterSelection.getCurrentTypes()
    if (!currentTypes?.length) {
      const types = this.createFilterTypesFromEnum()
      this.filterSelection.updateTypes(types)
      this.cdr.detectChanges()
    }
  }

  private updateItemSelection(
    category: FilterCategory,
    item: FilterItemType
  ): void {
    const items = this.getItemsByCategory(category)
    const index = items.findIndex(i => i.id === item.id)

    if (index !== -1) {
      items[index].selected = !items[index].selected
      this.updateSelectionByCategory(category, items)
      this.emitSelectedItems(category)
    }
  }

  private getItemsByCategory(category: FilterCategory): FilterItemType[] {
    switch (category) {
      case 'magazines':
        return [...this.filterSelection.getCurrentMagazines()]
      case 'authors':
        return [...this.filterSelection.getCurrentAuthors()]
      case 'types':
        return [...this.filterSelection.getCurrentTypes()]
      default:
        return []
    }
  }

  private updateSelectionByCategory(
    category: FilterCategory,
    items: FilterItemType[]
  ): void {
    switch (category) {
      case 'magazines':
        this.filterSelection.updateMagazines(items as FilterMagazine[])
        break
      case 'authors':
        this.filterSelection.updateAuthors(items as FilterAuthor[])
        break
      case 'types':
        this.filterSelection.updateTypes(items as FilterType[])
        break
    }
  }

  private isAnyDialogOpen(): boolean {
    const dialogConfig = this.dialogService.getCurrentConfig()
    return Object.values(dialogConfig).some(isOpen => isOpen)
  }

  private isClickInsideDialog(event: MouseEvent): boolean {
    const target = event.target as HTMLElement
    return !!(
      target.closest('dhp-generic-filter') ||
      target.closest('dhp-filter-item') ||
      target.closest('[id$="Dialog"]') ||
      target.closest('.filter-dialog-container')
    )
  }

  private keepDialogOpen(category: FilterCategory): void {
    const dialogMap: Record<FilterCategory, keyof FilterDialogConfig> = {
      authors: 'showAuthorDialog',
      magazines: 'showMagazineDialog',
      types: 'showTypeDialog',
    }

    const dialogKey = dialogMap[category]
    if (dialogKey) {
      this.dialogService.setDialogState(dialogKey, true)
    }
  }

  private getCategoryFromDialogType(
    dialog: keyof FilterDialogConfig
  ): FilterCategory | null {
    const dialogToCategory: Record<keyof FilterDialogConfig, FilterCategory> = {
      showMagazineDialog: 'magazines',
      showAuthorDialog: 'authors',
      showTypeDialog: 'types',
    }
    return dialogToCategory[dialog] ?? null
  }

  private calculateSelectedIds(category: FilterCategory): (number | string)[] {
    const items = this.getItemsByCategory(category)
    return items.filter(item => item.selected).map(item => item.id)
  }

  private emitSelectedItems(category: FilterCategory): void {
    const ids = this.calculateSelectedIds(category)
    const emitterName = `${category.slice(0, -1)}Selected` as EmitterName<
      typeof category
    >

    if (category === 'types') {
      ;(this[emitterName] as OutputEmitterRef<ArticleType[]>).emit(
        ids as ArticleType[]
      )
    } else {
      ;(this[emitterName] as OutputEmitterRef<number[]>).emit(ids as number[])
    }
  }

  private isLoading(category: FilterCategory): boolean {
    const loadingMap: Record<FilterCategory, boolean> = {
      magazines: this.isLoadingMagazines,
      types: this.isLoadingTypes,
      authors: this.isLoadingAuthors,
    }
    return loadingMap[category]
  }

  private setLoading(category: FilterCategory, loading: boolean): void {
    switch (category) {
      case 'magazines':
        this.isLoadingMagazines = loading
        break
      case 'types':
        this.isLoadingTypes = loading
        break
      case 'authors':
        this.isLoadingAuthors = loading
        break
    }
    this.cdr.detectChanges()
  }

  private loadMoreItems(category: FilterCategory): void {
    const loadMoreMap: Record<FilterCategory, () => void> = {
      magazines: () => {
        this.loadMoreMagazines(this.filterSelection.getCurrentMagazinePage())
      },
      types: () => {
        this.loadMoreTypes(this.filterSelection.getCurrentTypePage())
      },
      authors: () => {
        this.loadMoreAuthors(this.filterSelection.getCurrentAuthorPage())
      },
    }
    loadMoreMap[category]()
  }

  private loadMoreMagazines(page: number): void {
    if (this.magazines.length >= (this.totalMagazines ?? 0)) {
      this.setLoading('magazines', false)
      return
    }

    this.filterSelection
      .loadMoreMagazines(page + 1)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: newItems => {
          if (newItems.length > 0) {
            this.handleNewItems('magazines', newItems)
            this.magazinePage = page + 1
          }
          this.setLoading('magazines', false)
        },
        error: () => {
          this.setLoading('magazines', false)
        },
      })
  }

  private loadMoreTypes(page: number): void {
    this.filterSelection
      .loadMoreTypes(page + 1)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: newItems => {
          this.handleNewItems('types', newItems)
        },
        error: () => {
          this.setLoading('types', false)
        },
      })
  }

  private loadMoreAuthors(page: number): void {
    this.authorsService
      .getAuthors(page + 1, AUTHOR_PAGE_SIZE)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          if (response?.Authors?.docs) {
            const newAuthors = this.mapAuthorsResponseToFilterAuthors(
              response.Authors.docs
            )
            this.updateAuthorsWithNewItems(newAuthors)
            this.filterSelection.setCurrentAuthorPage(page + 1)
          }
          this.setLoading('authors', false)
        },
        error: () => {
          this.setLoading('authors', false)
        },
      })
  }

  private updateAuthorsWithNewItems(newAuthors: FilterAuthor[]): void {
    const currentAuthors = this.filterSelection.getCurrentAuthors()
    const selectedIds = currentAuthors.filter(a => a.selected).map(a => a.id)

    newAuthors.forEach(author => {
      author.selected = selectedIds.includes(author.id)
    })

    this.filterSelection.updateAuthors([...currentAuthors, ...newAuthors])
  }

  private handleNewItems(
    category: FilterCategory,
    newItems: FilterItemType[]
  ): void {
    if (!newItems?.length) {
      this.setLoading(category, false)
      return
    }

    const currentItems = this.getItemsByCategory(category)
    this.updateSelectionByCategory(category, [...currentItems, ...newItems])
    this.setLoading(category, false)
  }

  private cleanupAuthorSearch(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe()
      this.searchSubscription = null
    }
  }

  private setupAuthorDialog(): void {
    const currentAuthors = this.filterSelection.getCurrentAuthors()
    if (currentAuthors.length === 0) {
      this.isLoadingAuthors = true
      this.cdr.detectChanges()
      this.loadAuthors()
    }

    this.setupAuthorSearchSubscription()
  }
}
